/**
 * Slash menu ("/"): Notion-style block insertion.
 *
 * Labels arrive already localized from the component (useI18n cannot
 * be used here: this is not a Solid component).
 */
import { type Editor, Extension, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion, type SuggestionOptions } from "@tiptap/suggestion";

export interface SlashMenuItem {
  id: string;
  label: string;
  hint: string;
  /** Extra search terms (English/Spanish) in addition to label. */
  keywords: string;
  apply: (editor: Editor, range: Range) => void;
}

interface SlashMenuLabels {
  text: string;
  heading1: string;
  heading2: string;
  heading3: string;
  bulletList: string;
  orderedList: string;
  quote: string;
  codeBlock: string;
  divider: string;
  hints: {
    text: string;
    heading1: string;
    heading2: string;
    heading3: string;
    bulletList: string;
    orderedList: string;
    quote: string;
    codeBlock: string;
    divider: string;
  };
}

export function buildSlashMenuItems(labels: SlashMenuLabels): SlashMenuItem[] {
  return [
    {
      id: "text",
      label: labels.text,
      hint: labels.hints.text,
      keywords: "text texto paragraph parrafo p",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).setParagraph().run();
      },
    },
    {
      id: "heading1",
      label: labels.heading1,
      hint: labels.hints.heading1,
      keywords: "h1 heading titulo encabezado",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).setNode("heading", { level: 1 }).run();
      },
    },
    {
      id: "heading2",
      label: labels.heading2,
      hint: labels.hints.heading2,
      keywords: "h2 heading subtitulo encabezado",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).setNode("heading", { level: 2 }).run();
      },
    },
    {
      id: "heading3",
      label: labels.heading3,
      hint: labels.hints.heading3,
      keywords: "h3 heading encabezado",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).setNode("heading", { level: 3 }).run();
      },
    },
    {
      id: "bulletList",
      label: labels.bulletList,
      hint: labels.hints.bulletList,
      keywords: "bullet list lista viñetas ul",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run();
      },
    },
    {
      id: "orderedList",
      label: labels.orderedList,
      hint: labels.hints.orderedList,
      keywords: "ordered numbered list lista numerada ol",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run();
      },
    },
    {
      id: "quote",
      label: labels.quote,
      hint: labels.hints.quote,
      keywords: "quote blockquote cita",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleBlockquote().run();
      },
    },
    {
      id: "codeBlock",
      label: labels.codeBlock,
      hint: labels.hints.codeBlock,
      keywords: "code codigo bloque snippet",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
      },
    },
    {
      id: "divider",
      label: labels.divider,
      hint: labels.hints.divider,
      keywords: "divider separator hr divisor separador linea",
      apply: (editor, range) => {
        editor.chain().focus().deleteRange(range).setHorizontalRule().run();
      },
    },
  ];
}

export function createSlashMenuExtension(options: {
  items: SlashMenuItem[];
  render: SuggestionOptions<SlashMenuItem, SlashMenuItem>["render"];
}) {
  return Extension.create({
    name: "slashMenu",

    addProseMirrorPlugins() {
      return [
        Suggestion<SlashMenuItem, SlashMenuItem>({
          editor: this.editor,
          char: "/",
          pluginKey: new PluginKey("slash-menu"),
          allowSpaces: false,
          command: ({ editor, range, props }) => {
            props.apply(editor, range);
          },
          items: ({ query }) => {
            const q = query.trim().toLowerCase();
            if (q.length === 0) {
              return options.items;
            }
            return options.items.filter((item) =>
              `${item.label} ${item.keywords}`.toLowerCase().includes(q),
            );
          },
          render: options.render,
        }),
      ];
    },
  });
}
